import { Box, Button, styled, Typography, type ButtonProps } from '@mui/material';

import { useSelectAlphabeticalLetter } from '../useSelectAlphabeticalLetter';

export const StyledRoot = styled(Box)({
  minWidth: '260px',
});

export const StyledHeader = styled(Typography)(({ theme }) => ({
  borderBottom: theme.shape.divider,
  paddingBottom: theme.spacing(1.5),
})) as typeof Typography;

type StyledLetterButtonProps = ButtonProps & {
  isSelected: boolean;
};

export const StyledLetterButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'isSelected',
})<StyledLetterButtonProps>(({ theme, isSelected }) => ({
  minWidth: '40px',
  minHeight: '40px',
  padding: 0,
  borderRadius: '50%',
  color: isSelected ? theme.palette.common.white : theme.palette.neutral[700],
  backgroundColor: isSelected ? theme.palette.primary.main : theme.palette.common.white,
  ...theme.typography['Title/small'],

  '&:hover': {
    backgroundColor: theme.palette.custom.grey,
    color: theme.palette.neutral[700],
  },
}));

type AlphabetContainerProps = {
  title: string;
  alphabet?: string[];
  onSelected: () => void;
};

export const AlphabetContainer = (props: AlphabetContainerProps) => {
  const { title, alphabet, onSelected } = props;
  const hasAlphabet = !!alphabet;

  const { isNumericSearchQuery, alphabeticalIndexSearchQuery, selectLetter } = useSelectAlphabeticalLetter({
    onSelected,
  });

  return (
    <StyledRoot>
      <StyledHeader component="div" textAlign="center" variant="Label/large" color="neutral.700">
        {title}
      </StyledHeader>
      {hasAlphabet && (
        <Box
          sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 0fr)', mt: 0.5 }}
          data-testid="alphabetical-index-letters-block"
        >
          {alphabet.map((letter, index) => (
            <StyledLetterButton
              isSelected={letter === alphabeticalIndexSearchQuery}
              key={index}
              onClick={() => selectLetter(letter)}
            >
              {letter}
            </StyledLetterButton>
          ))}
        </Box>
      )}
      {!hasAlphabet && (
        <Box sx={{ textAlign: 'center', mt: 0.5 }} data-testid="alphabetical-index-numbers-block">
          <StyledLetterButton isSelected={isNumericSearchQuery} onClick={() => selectLetter('0-9')}>
            0-9
          </StyledLetterButton>
        </Box>
      )}
    </StyledRoot>
  );
};
